import type { Language } from './i18n'
import type { ClientRow, LeadClient, LeadStatus } from './types'

export function clientDisplayName(client?: ClientRow | LeadClient | null, fallback = '—') {
  if (!client) return fallback
  if (client.client_type === 'company') return client.company_name || fallback
  return [client.first_name, client.last_name].filter(Boolean).join(' ') || client.instagram_handle || client.email || client.phone || fallback
}

export function formatMoney(value: number | null | undefined, language: Language) {
  return new Intl.NumberFormat(language === 'pl' ? 'pl-PL' : 'ru-RU', {
    style: 'currency',
    currency: 'PLN',
    maximumFractionDigits: 2
  }).format(value || 0)
}

export function formatDate(value: string | null | undefined, language: Language, includeTime = false) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(language === 'pl' ? 'pl-PL' : 'ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {})
  }).format(date)
}

export function sourceName(source: { name_pl: string; name_ru: string } | null | undefined, language: Language) {
  if (!source) return '—'
  return language === 'pl' ? source.name_pl : source.name_ru
}

export function serviceName(service: { name_pl: string; name_ru: string } | null | undefined, language: Language) {
  if (!service) return '—'
  return language === 'pl' ? service.name_pl : service.name_ru
}

const statusLabels: Record<LeadStatus, { pl: string; ru: string }> = {
  new: { pl: 'Nowe', ru: 'Новый' },
  contact_in_progress: { pl: 'Kontakt w toku', ru: 'Коммуникация' },
  collecting_information: { pl: 'Zbieranie informacji', ru: 'Уточнение информации' },
  preparing_quote: { pl: 'Przygotowanie wyceny', ru: 'Подготовка расчёта' },
  offer_sent: { pl: 'Oferta wysłana', ru: 'Предложение отправлено' },
  waiting_for_client: { pl: 'Oczekiwanie na klienta', ru: 'Ожидаем клиента' },
  won: { pl: 'Wygrane', ru: 'Подтверждено' },
  lost: { pl: 'Przegrane', ru: 'Отказ' },
  postponed: { pl: 'Odłożone', ru: 'Отложено' }
}

export function leadStatusLabel(status: LeadStatus, language: Language) {
  return statusLabels[status][language]
}

export function leadStatusTone(status: LeadStatus) {
  if (status === 'won') return 'success'
  if (status === 'lost') return 'danger'
  if (status === 'waiting_for_client' || status === 'postponed') return 'warning'
  if (status === 'offer_sent' || status === 'preparing_quote') return 'accent'
  return 'neutral'
}

export const leadStatusOptions: LeadStatus[] = [
  'new',
  'contact_in_progress',
  'collecting_information',
  'preparing_quote',
  'offer_sent',
  'waiting_for_client',
  'won',
  'lost',
  'postponed'
]
